import {Clone} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useEffect, useMemo, useState} from "react";
import {Button, Column, Row, Spinner, Text, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {Slider, TextPlus, RunInAction_Set} from "web-vcore";
import {SceneryKindDef, type ParamOverride, type ParamSpread, type ResolvedSceneryParam, mergeParamOverride, effectiveParamValue, effectiveParamSwing, toParamSpread, groupSceneryParams} from "../../../../../../Terrain/Scenery/@SceneryTypes.js";
import {type ParamValueInRecipe} from "../../../../../../Terrain/Materials/@MaterialTypes.js";
import {GetSceneryKindLineage} from "../../../../../../Server/DB/mapObjects/SceneryAccessors.js";
import {bakeKindPreview} from "../../../../../../Terrain/Scenery/Bake/SceneryPreviewBaker.js";
import {kindTextureTypeIDs} from "../../../../../../Terrain/Scenery/Bake/ImpostorBaker.js";
import {texturesReady} from "../../../../../../Terrain/Scenery/Bake/SceneryTextures.js";
import {DEFAULT_BAKE_LIGHT_RIG} from "../../../../../../Terrain/Scenery/Bake/ImpostorBaker.js";
import {sliderFlex} from "../../../../../../Utils/UI/GlobalStyles.js";
import {ParamValueEditor} from "../TerrainParams/ParamValueEditor.js";
import {GetMapState} from "../../../../../../Store/main/maps/$mapState.js";

/** KindEditor — Set13 P6: the full scenery-kind editor.  Name/JSON/lineage, the auto-generated
 *  param sliders (TWO per param: value + swing, the §4.3 partial-patch UI), a read-only derived
 *  `[min … max]` readout, the placement defaults, and a VARIANT PREVIEW STRIP of the baked atlas
 *  cells (the single most useful authoring affordance — "are my 6 variants actually different?").
 *
 *  ⚠ Each slider writes ONLY its own field of the param's `ParamSpread` patch (§4.3 b): the
 *  value slider writes `{value}`, the swing slider writes `{swing}`.  This is exactly what the
 *  partial-patch semantics exist to allow — a value edit must never reset the inherited swing.
 */
export const KindEditor = observer_mgl((props: {
	mapID: string;
	kind: SceneryKindDef;
	onChange: (kind: SceneryKindDef)=>void;
	onClone: ()=>void;
	onDelete: ()=>void;
})=>{
	const {mapID, kind, onChange, onClone, onDelete} = props;
	const lineage = GetSceneryKindLineage(kind.id, mapID);
	const resolved = lineage.resolved;

	const ChangeKind = (mutator: (k: SceneryKindDef)=>any)=>{
		const newValue = Clone(kind);
		mutator(newValue);
		onChange(newValue);
	};

	// The single sparse `paramOverrides` patch, keyed by the resolved param key.  Each edit here
	// mutates a CLONE of the whole overrides map, so mobx tracks the nested change.  `mergeParamOverride`
	// applies the PARTIAL patch on top of the existing override — only the edited field changes (§4.3 b).
	const setParam = (key: string, patch: ParamSpread)=>{
		ChangeKind(k=>{
			const next = {...k.paramOverrides};
			const merged = mergeParamOverride(next[key], patch);
			if (merged === undefined) delete next[key];
			else next[key] = merged;
			k.paramOverrides = next;
		});
	};

	return (
		<Column mt={5} style={{background: "rgba(255,255,255,.15)", padding: 5, borderRadius: 5}}>
			<Row>
				<TextInput ml={0} style={{flex: 1}} value={kind.name} onChange={val=>ChangeKind(k=>k.name = val)}/>
				<Button ml={5} p="3px 5px" text="JSON" onClick={()=>{
					let newJSON = JSON.stringify(kind, undefined, "\t");
					const controller = ShowMessageBox({
						title: "JSON of kind (editable)", cancelButton: true,
						okOnEnterKey: false, // disable Enter hotkey, so user can add lines to text-area
						message: ()=><Column style={{width: 600}}>
							<textarea style={{width: "100%", height: 500}} value={newJSON} onChange={e=>{
								newJSON = e.target.value;
								controller.UpdateUI();
							}}/>
						</Column>,
						onOK: ()=>{
							const dataFromDialog = JSON.parse(newJSON);
							ChangeKind(k=>{
								for (const key of Object.keys(k)) delete (k as any)[key];
								Object.assign(k, dataFromDialog);
							});
						}
					});
				}}/>
				<Button ml={5} mdIcon="content-copy" title="Clone this kind" onClick={()=>onClone()}/>
				<Button ml={5} mdIcon="delete" onClick={()=>{
					ShowMessageBox({
						title: "Delete kind?", cancelButton: true,
						message: "Permanently delete this custom scenery kind?",
						onOK: ()=>onDelete(),
					});
				}}/>
			</Row>
			<Row mt={5}>
				<Text>Base:</Text>
				<TextInput ml={5} style={{flex: 1}} value={kind.baseKindID ?? ""}
					onChange={val=>ChangeKind(k=>k.baseKindID = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					Inheritance lineage (root → this kind).  A "variant" (extend) live-links to its base:
					base edits propagate.  A "fork" is a detached deep copy.
				`.AsMultiline(0)}>Lineage:</TextPlus>
				<Text ml={5} style={{fontSize: 12, opacity: 0.8}}>{lineage.chain.join(" → ")}{lineage.error ? ` (${lineage.error})` : ""}</Text>
			</Row>
			{lineage.error && <Row mt={2}><Text style={{color: "#f88", fontSize: 12}}>{lineage.error}</Text></Row>}

			{resolved && <>
				<Row mt={8} style={{fontWeight: "bold"}}>
					<Text>Seed:</Text>
					{/* Set14 P2 — the kind's SEED: the base for the per-variant seed (§4.6).  The reroll
					    button sets it to a random integer — the single highest-value authoring affordance
					    ("I don't like this kind's whole family — give me another"). */}
					<Spinner ml={8} style={{width: 90}} step={1} value={kind.seed} placeholder={""+kind.seed}
						onChange={val=>ChangeKind(k=>k.seed = val != null && Number.isFinite(val) ? Math.floor(val) : 0)}/>
					<Button ml={5} mdIcon="refresh" title="Reroll the kind's seed (new variant family)"
						onClick={()=>ChangeKind(k=>k.seed = Math.floor(Math.random() * 0xffffffff))}/>
				</Row>
				<Row mt={2} style={{opacity: 0.6, fontSize: 11}}>
					<Text>Each param has TWO sliders: <b>value</b> (the resolved default) and <b>swing</b> (how much it varies
						between variants — half-width, §4.3 a).  The read-only <code>[min … max]</code> shows the resulting
						sampling span.  Params without a swing (0) are identical in every variant (§3.2 d) — a fixed value
						costs no extra atlas cell.  The seed decorrelates this kind's variant family from every other's.</Text>
				</Row>
				<ParamGroups
					mapID={mapID}
					params={resolved.params}
					overrides={kind.paramOverrides}
					onSetParam={(key, patch)=>setParam(key, patch)}/>

				<Row mt={8} style={{fontWeight: "bold"}}><Text>Placement defaults:</Text></Row>
				<Row mt={2} style={{opacity: 0.6, fontSize: 11}}>
					<Text>A path's scenery feature may override these per-region (path → Scenery tab).</Text>
				</Row>
				<Row mt={5}>
					<TextPlus info={`Mean spacing in metres between placement candidates (a PathSceneryFeature may override).`}>Spacing (m):</TextPlus>
					<Spinner ml={5} step="any" min={0} value={kind.spacing} onChange={val=>ChangeKind(k=>k.spacing = val != null && val > 0 ? val : 6)}/>
				</Row>
				<Row mt={3}>
					<TextPlus info={`Fraction of candidate cells that actually spawn (0..1) — the density knob.`}>Presence:</TextPlus>
					<Spinner ml={5} step="any" min={0} max={1} value={kind.presence} onChange={val=>ChangeKind(k=>k.presence = val != null && val >= 0 && val <= 1 ? val : 0.8)}/>
				</Row>
				<Row mt={3}>
					<TextPlus info={`How many structural variants to derive (LHS-sampled, §3.2). Half a dozen is plenty.`}>Variants:</TextPlus>
					<Spinner ml={5} step={1} min={1} max={32} value={kind.variantCount} onChange={val=>ChangeKind(k=>k.variantCount = val != null && val >= 1 ? Math.round(val) : 6)}/>
				</Row>

				<VariantPreviewStrip key={`${kind.id}:${resolved.structuralHash}`} resolved={resolved}/>
			</>}
		</Column>
	);
});

/** Set14 P2 — renders the resolved params GROUPED by `ParamSchema.group` (declaration order within
 *  each group; ungrouped params land in "General").  Each group is a collapsible section.  Uses
 *  the param's own `group` field (§4.3 c) so the UI never hard-codes a group list. */
const ParamGroups = observer_mgl((props: {
	mapID: string;
	params: ResolvedSceneryParam[];
	overrides: {[key: string]: ParamOverride};
	onSetParam: (key: string, patch: ParamSpread)=>void;
})=>{
	const {mapID, params, overrides, onSetParam} = props;
	// Set14 P2 — collapse state is PERSISTED per-kind in `MapState.propertiesPanel.collapsedGroups`
	// (a Map<string, boolean>), so it survives remounts of the KindEditor (switching kinds/tabs).
	const collapsedGroups = GetMapState(mapID)?.propertiesPanel.terrainSceneries_collapsedGroups;
	const collapsed = (group: string)=>collapsedGroups?.get(group) ?? false;
	const setCollapsed = (group: string, value: boolean)=>RunInAction_Set(()=>collapsedGroups?.set(group, value));
	// Partition by the param's own `group` (declaration order within a group; ungrouped → "General").
	const groups = groupSceneryParams(params);
	return (
		<>
			{groups.map(g=>{
				const isCollapsed = collapsed(g.name);
				return (
					<Column key={g.name} mt={4}>
						<Row style={{cursor: "pointer", fontWeight: "bold", fontSize: 13, opacity: 0.9}}
							onClick={()=>setCollapsed(g.name, !isCollapsed)}>
							<Text>{isCollapsed ? "▸" : "▾"} {g.name} ({g.params.length})</Text>
						</Row>
						{!isCollapsed && g.params.map(p=>(
							<ParamEditorRow key={p.key} param={p} override={overrides[p.key]} onChange={patch=>onSetParam(p.key, patch)}/>
						))}
					</Column>
				);
			})}
		</>
	);
});

/** One param's editor row — the SHARED kind-dispatching value editor (scalar slider / colour
 *  picker / ramp, the exact control the material RecipeEditor uses) PLUS a swing slider + a
 *  read-only `[min … max]` readout.  Each writes only its own field of the `ParamSpread` patch. */
const ParamEditorRow = observer_mgl((props: {
	param: ResolvedSceneryParam;
	override: ParamOverride | undefined;
	onChange: (patch: ParamSpread)=>void;
})=>{
	const {param, override, onChange} = props;
	// Resolve the effective value + swing through the override patch (partial — only present fields apply).
	const value = effectiveParamValue(override, param.value);
	const swing = effectiveParamSwing(override, param.swing);
	const hasSwingDefault = param.schema.swingDefault != null;
	// Whether the override explicitly patches a field (drives the reset-to-inherit button state).
	const valueIsPatched = override != null && toParamSpread(override).value != null;
	const swingIsPatched = override != null && toParamSpread(override).swing != null;
	// Set14 P2 — scalar AND int params are swingable (int strata round to integers); enum/bool/
	// vec3 are never swung (§4.3 a / §4.13) — no swing row, no span readout, value passed through.
	const isSwingable = param.schema.kind == "scalar" || param.schema.kind == "int";

	// The read-only derived span (§5 P6): [max(min, value-swing), min(max, value+swing)], the
	// INTERSECTED span §3.2 b/c stratifies over.  Numeric-swingable only (int/enum/bool/vec3/color
	// /ramp are either never swung or not numeric strata).
	const min = typeof param.schema.min == "number" ? param.schema.min : 0;
	const max = typeof param.schema.max == "number" ? param.schema.max : 1;
	const v = isSwingable && typeof value == "number" ? value : (isSwingable && typeof param.schema.default == "number" ? param.schema.default : 0);
	const spanLo = isSwingable ? Math.max(min, v - swing) : 0;
	const spanHi = isSwingable ? Math.min(max, v + swing) : 0;

	// The effective value as a `ParamValueInRecipe` for the shared editor: `undefined` = inherit
	// (only when the patch doesn't touch value).  Colours/ramps/enums/bools/vec3s pass through as-is.
	const editorValue: ParamValueInRecipe = isSwingable ? (valueIsPatched && typeof value == "number" ? value : undefined)
		: (valueIsPatched ? value : undefined);

	return (
		<Column mt={4} style={{background: "rgba(255,255,255,.08)", padding: 4, borderRadius: 4}}>
			{/* The shared value editor renders its own label + value control (kind-dispatched).  The
			    swing row below adds the scenery-only swing control. */}
			<ParamValueEditor
				// `fallback` = the fully-resolved value WITHOUT this kind's own override (`param.fallback`),
				// not `param.schema.default`: an in-between chain kind may override via `paramOverrides`,
				// and `param.fallback` carries the root→parent effective value (what inherit shows).
				param={{key: param.key, label: param.schema.label || param.key, kind: param.schema.kind, min, max, fallback: param.fallback, options: param.schema.options}}
				value={editorValue}
				onChange={v=>onChange({value: v})}
				info={`${param.key} — min ${min}, max ${max}.  ${hasSwingDefault ? `swing ${param.swing} (half-width)`.trim() : "no swing (fixed in every variant)"}.`}
				title={param.key}
				canReset={valueIsPatched}
				resetTitle="Reset value (inherit)"
				extraLabel={isSwingable ? <Text ml={5} style={{fontSize: 11, opacity: 0.6}}>[{spanLo.toFixed(1)} … {spanHi.toFixed(1)}]</Text> : null}/>
			{hasSwingDefault && isSwingable && <Row mt={3}>
				<Text style={{minWidth: 45, fontSize: 11, opacity: 0.7}}>Swing:</Text>
				<Slider min={0} max={Math.max(max - min, 0.1)} step={Math.max((max - min) / 200, 1e-4)}
					{...sliderFlex} value={swing} onChange={val=>onChange({swing: val})}/>
				<Spinner ml={5} placeholder={""+param.schema.swingDefault}
					value={swing} onChange={val=>onChange({swing: val})}/>
				<Button ml={3} mdIcon="delete" enabled={swingIsPatched} title="Reset swing (inherit)"
					onClick={()=>onChange({swing: undefined as any})}/>
			</Row>}
		</Column>
	);
});

/** The VARIANT PREVIEW STRIP — the baked atlas cells side by side (§5 P6).  The single most useful
 *  authoring affordance: "are my 6 variants actually different?" answerable at a glance.  Bakes
 *  off-frame via `bakeKindPreview` (throwaway WebGL2, never the live frame); degrades to a
 *  placeholder when WebGL is unavailable. */
const VariantPreviewStrip = observer_mgl((props: {resolved: any})=>{
	const {resolved} = props;
	// Re-bake whenever the resolved kind changes (structural hash changes with params/swing/seed).
	// Set14 P4 §4.9 — the preview's bake uses the texture cache, which loads ASYNC. `bakeKindPreview`
	// returns null (and kicks off the preload) until the kind's textures are ready; we poll a
	// readiness tick so the preview re-bakes once they arrive (a pre-texture bake would otherwise
	// stick as a flat untextured preview — the §4.9 "not cached as final" rule).
	const [readyTick, setReadyTick] = useState(0);
	useEffect(()=>{
		const id = setInterval(()=>{
			const {barkTypes, leafTypes} = kindTextureTypeIDs(resolved);
			if (texturesReady(barkTypes, leafTypes)) {
				clearInterval(id);
				setReadyTick(t=>t + 1); // trigger a re-render → useMemo re-runs the now-ready bake
			}
		}, 250);
		return ()=>clearInterval(id);
	}, [resolved]);
	const preview = useMemo(()=>bakeKindPreview(resolved, DEFAULT_BAKE_LIGHT_RIG), [resolved, readyTick]);

	if (preview == null) {
		return (
			<Row mt={8} style={{opacity: 0.6, fontSize: 11}}>
				<Text>Variant preview unavailable (WebGL2 not available here).</Text>
			</Row>
		);
	}
	return (
		<Column mt={8}>
			<Row style={{fontWeight: "bold"}}><Text>Variant preview:</Text></Row>
			<Row mt={3} style={{flexWrap: "wrap", gap: 4}}>
				{preview.cells.map((src, i)=>(
					<Column key={i} style={{alignItems: "center"}}>
						<img src={src} width={48} height={48} style={{background: "#000", border: "1px solid rgba(255,255,255,.3)", borderRadius: 3}}/>
						<Text style={{fontSize: 10, opacity: 0.7}}>v{i}</Text>
					</Column>
				))}
			</Row>
			<Row mt={2} style={{opacity: 0.6, fontSize: 11}}>
				<Text>Top-down bake of each structural variant (LHS-sampled from the swings above).  Distinct cells = real variety.</Text>
			</Row>
		</Column>
	);
});
